import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  BarChart3,
  Check,
  CheckCircle2,
  Clock3,
  Lock,
  PauseCircle,
  Play,
  Shield,
  Users2,
} from "lucide-react";

import ParticleField from "../components/atmos/ParticleField";
import { listProfiles } from "../lib/profileEngine";
import { getGrantedProfiles, getLockedProfileCount } from "../lib/profileVisibility";
import { panelPhotoStyle } from "../lib/siteImages";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (index = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const journeyCards = [
  {
    title: "Participant access",
    text: "Sign in once with your Redacamy account, then open your profiles from your course and track active, paused, and completed profiles on your dashboard.",
    icon: Users2,
    variant: "journey",
    image: "analytics.png",
  },
  {
    title: "Automated scoring",
    text: "Grouped item responses feed factor-based scoring for expectation, present behavior, and variance reporting.",
    icon: BarChart3,
    variant: "analytics",
    image: "profile-folder.png",
  },
  {
    title: "Profile tracking",
    text: "Time logging, revisit monitoring, response-change tracking, and controlled retake timing support delivery integrity.",
    icon: Clock3,
    variant: "timing",
    image: "timing.png",
  },
];

const Landing = () => {
  const [profiles, setProfiles] = useState([]);

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      try {
        const rows = await listProfiles();
        if (!ignore) {
          setProfiles(rows);
        }
      } catch {
        if (!ignore) {
          setProfiles([]);
        }
      }
    };

    load();
    return () => {
      ignore = true;
    };
  }, []);

  const publicProfiles = getGrantedProfiles(profiles);
  const featuredProfile = publicProfiles[0] || null;
  const inviteOnlyCount = getLockedProfileCount(profiles);

  return (
    <main className="relative isolate min-h-[calc(100vh-3.5rem)] lg:min-h-screen overflow-hidden lg:pl-24 xl:pl-28" data-testid="landing-page">
      <div className="fixed inset-0 -z-10">
        <ParticleField density={64} />
        <div className="orb orb-amber w-[420px] h-[420px] md:w-[700px] md:h-[700px] -top-28 md:-top-40 -left-28 md:-left-40 float-slow" />
        <div className="orb orb-deep w-[320px] h-[320px] md:w-[560px] md:h-[560px] top-1/2 -right-16 md:-right-24 float-slow" style={{ animationDelay: "-4s" }} />
        <div className="orb orb-teal w-[300px] h-[300px] md:w-[500px] md:h-[500px] bottom-0 left-1/3 float-slow" style={{ animationDelay: "-8s" }} />
      </div>

      <section className="relative flex items-start pb-4 md:pb-6 pt-8 md:pt-10 lg:pt-12 px-5 md:px-10 lg:px-16">
        <div className="relative w-full grid grid-cols-12 gap-5 md:gap-6 items-start">
          <div className="col-span-12 lg:col-span-8">
            <motion.div variants={fadeUp} initial="hidden" animate="show" className="flex items-center gap-3 mb-3 md:mb-4">
              <div className="h-px w-10 bg-gold" />
              <span className="font-mono text-[16px] md:text-[16px] tracking-widest-2 uppercase text-ink-2">
                DIGITAL SPIRITUAL PROFILE PLATFORM
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={1}
              className="font-serif font-light tracking-tight leading-[0.92] text-ink max-w-[11ch] md:max-w-none"
              style={{ fontSize: "clamp(30px, 6.8vw, 72px)" }}
            >
              Spiritual development,<br />
              <span className="italic text-gold gold-glow">measured with clarity.</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={2}
              className="mt-4 md:mt-5 max-w-xl text-ink-2 text-base md:text-base leading-relaxed"
            >
              A web-based platform for digitizing Dr. Richard P. Johnson&apos;s spiritual and life-stage
              profiles. Participants can complete structured profile items online, generate scored
              interpretations, and download personalized reports with visual summaries.
            </motion.p>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={3}
              className="mt-6 md:mt-7 flex flex-wrap gap-3"
            >
              <Link to="/dashboard" className="btn-gold landing-btn" data-testid="landing-dashboard">
                Open My Dashboard <ArrowUpRight className="w-4 h-4" />
              </Link>
              <a href="#about" className="btn-ghost landing-btn" data-testid="landing-about">
                Learn More
              </a>
            </motion.div>
          </div>

          <motion.aside
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={4}
            className="col-span-12 lg:col-span-4 self-start mt-2 lg:mt-1"
          >
            <div className="glass rounded-sm p-5">
              <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-3">◐ PLATFORM ACCESS</div>
              <div className="font-serif text-[1.7rem] md:text-[1.9rem] text-ink leading-tight">
                Secure participant profile experience
              </div>
              <div className="text-ink-2 mt-2 text-base leading-relaxed">
                Structured profile items, protected report access, and guided participant progress in one place.
              </div>
              <div className="h-px bg-line my-4" />
              <div className="space-y-3 text-[16px] text-ink-2 leading-relaxed">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-gold mt-0.5" />
                  <span>Participant-facing profile flow with saved progress and guided completion</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-gold mt-0.5" />
                  <span>Automated interpretation reports with secure viewing and PDF download support</span>
                </div>
              </div>
            </div>
          </motion.aside>
        </div>
      </section>

      <section className="px-5 md:px-10 lg:px-16 py-7 md:py-9">
        <div className="grid grid-cols-12 gap-3 md:gap-4">
          <motion.article
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="col-span-12 lg:col-span-7 glass rounded-sm overflow-hidden"
          >
            <div className="grid lg:grid-cols-[1.05fr_0.95fr]">
              <div className="p-5 md:p-6 lg:p-7">
                <div className="font-mono text-[16px] tracking-widest-2 text-gold mb-3">◐ PLATFORM OVERVIEW</div>
                <h2 className="font-serif text-[2.15rem] md:text-[2.55rem] xl:text-[2.85rem] leading-[0.96] text-ink max-w-[13ch] xl:max-w-none">
                  Digital profile delivery with structured reporting.
                </h2>
                <p className="mt-4 text-base md:text-base text-ink-2 max-w-xl leading-relaxed">
                  The platform is designed to digitize Dr. Richard P. Johnson&apos;s spiritual and life-stage
                  profiles, allowing participants to complete structured profile items online and
                  receive scored interpretations with graphical summaries.
                </p>
              </div>
              <div
                className="min-h-[300px] lg:min-h-full assessment-visual-panel assessment-visual-panel-main"
                style={panelPhotoStyle("profile-delivery.png")}
              />
            </div>
          </motion.article>

          <motion.article
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.08 }}
            className="col-span-12 lg:col-span-5 glass rounded-sm overflow-hidden"
          >
            <div
              className="assessment-visual-panel assessment-visual-panel-report min-h-[230px] md:min-h-[260px]"
              style={panelPhotoStyle("report-delivery.png")}
            />
            <div className="p-5 md:p-6">
              <div className="font-mono text-[16px] tracking-widest-2 text-gold mb-3">◐ REPORTING AND RESULTS</div>
              <h3 className="font-serif text-[1.75rem] md:text-[2rem] text-ink leading-none">Personalized report delivery</h3>
              <p className="mt-3 text-base text-ink-2 leading-relaxed">
                Completed profiles produce downloadable report outputs with score visuals, interpretation sections,
                and private participant-facing result access.
              </p>
            </div>
          </motion.article>
        </div>
      </section>

      <section id="about" className="px-5 md:px-10 lg:px-16 py-8 md:py-10 border-y border-line">
        <div className="grid grid-cols-12 gap-4 md:gap-5 items-start">
          <div className="col-span-12 xl:col-span-4">
            <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-3">◇ ABOUT</div>
            <h2 className="font-serif text-[2.3rem] md:text-[2.9rem] xl:text-[3.2rem] text-ink leading-[0.98] max-w-[10ch] xl:max-w-none">
              Profile delivery,
              <span className="italic text-gold"> scoring, and reporting.</span>
            </h2>
            <p className="mt-4 max-w-sm text-base text-ink-2 leading-relaxed">
              This section highlights the participant-facing platform experience through profile delivery,
              saved progress, and secure report access.
            </p>
          </div>
          <div className="col-span-12 xl:col-span-8 grid md:grid-cols-2 gap-3">
            <motion.article
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.65 }}
              className="glass rounded-sm p-5 overflow-hidden"
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <div className="font-mono text-[16px] tracking-widest-2 text-ink">ASSESSMENT FLOW</div>
                  <h3 className="font-serif text-[1.55rem] md:text-[1.9rem] text-ink leading-tight mt-2">Profile item delivery</h3>
                </div>
                <div className="h-9 w-9 rounded-full border border-line flex items-center justify-center text-gold">
                  <Users2 className="w-4 h-4" />
                </div>
              </div>

              <div className="relative rounded-sm border border-line bg-white/[0.02] px-4 py-5">
                <div className="hidden sm:block absolute left-[22%] right-[22%] top-[42px] h-px bg-gradient-to-r from-transparent via-[#7f95b8] to-transparent opacity-80" />
                <div className="grid grid-cols-3 gap-3 items-start">
                  {[
                    { step: "01", label: "Begin", active: true },
                    { step: "02", label: "Respond", active: true },
                    { step: "03", label: "Resume", active: false },
                  ].map((item, index) => (
                    <div key={item.step} className="flex flex-col items-center text-center">
                      <motion.div
                        animate={
                          item.active
                            ? { scale: [1, 1.06, 1], opacity: [0.92, 1, 0.92] }
                            : { scale: 1, opacity: 0.65 }
                        }
                        transition={{ duration: 2.6, repeat: Infinity, delay: index * 0.25 }}
                        className={`relative h-12 w-12 rounded-full border flex items-center justify-center ${
                          item.active
                            ? "border-[#8ea2c4] bg-[#8ea2c4]/12 text-[#dfe8f8]"
                            : "border-line text-ink-3"
                        }`}
                      >
                        <span className="font-mono text-[16px]">{item.step}</span>
                      </motion.div>
                      <div className="mt-3 font-mono text-[16px] text-ink">{item.label}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-sm border border-line bg-black/10 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="relative h-12 w-12">
                      <svg viewBox="0 0 48 48" className="h-12 w-12 -rotate-90">
                        <circle cx="24" cy="24" r="18" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
                        <motion.circle
                          cx="24"
                          cy="24"
                          r="18"
                          fill="none"
                          stroke="#8ea2c4"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeDasharray={113}
                          animate={{ strokeDashoffset: [68, 40, 68] }}
                          transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
                        />
                      </svg>
                      <PauseCircle className="absolute inset-0 m-auto h-4 w-4 text-[#dfe8f8]" />
                    </div>
                    <div>
                      <div className="font-serif text-xl text-ink">Session Saved</div>
                      <div className="font-mono text-[16px] sm:text-[16px] tracking-widest-2 text-ink mt-1">PAUSE AND RESUME CAPABILITY</div>
                    </div>
                  </div>
                  <div className="self-start sm:self-auto rounded-full border border-[#8ea2c4]/30 px-3 py-1.5 font-mono text-[16px] tracking-widest-2 text-[color:var(--info-text)]">
                    65%
                  </div>
                </div>
              </div>
            </motion.article>

            <motion.article
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.65, delay: 0.1 }}
              className="glass rounded-sm p-5 overflow-hidden"
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <div className="font-mono text-[16px] tracking-widest-2 text-ink">REPORT OUTPUT</div>
                  <h3 className="font-serif text-[1.55rem] md:text-[1.9rem] text-ink leading-tight mt-2">Private report output</h3>
                </div>
                <div className="h-9 w-9 rounded-full border border-line flex items-center justify-center text-gold">
                  <Shield className="w-4 h-4" />
                </div>
              </div>

              <div className="relative min-h-[380px] rounded-sm border border-line bg-white/[0.02] overflow-hidden pb-16">
                <motion.div
                  animate={{ y: [0, -4, 0], rotate: [-5, -3, -5] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute left-8 top-8 h-56 w-40 rounded-sm border border-line bg-cover bg-center shadow-[0_18px_60px_rgba(0,0,0,0.25)]"
                  style={panelPhotoStyle("journey.png")}
                />
                <motion.div
                  animate={{ y: [0, -5, 0], rotate: [5, 3, 5] }}
                  transition={{ duration: 4.7, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute right-8 top-10 h-56 w-40 rounded-sm border border-line bg-cover bg-center shadow-[0_18px_60px_rgba(0,0,0,0.25)]"
                  style={panelPhotoStyle("pdf-download.png")}
                />
                <div className="absolute left-4 right-4 bottom-4 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1 rounded-full border border-line bg-black/10 px-2 py-1 text-[12px] text-ink-2 whitespace-nowrap">
                    <Lock className="h-3 w-3 text-gold shrink-0" />
                    SECURE ACCESS
                  </div>
                  <div className="flex items-center gap-1 rounded-full border border-line bg-black/10 px-2 py-1 text-[12px] text-ink-2 whitespace-nowrap">
                    <Check className="h-3 w-3 text-gold shrink-0" />
                    PDF DOWNLOAD
                  </div>
                </div>
              </div>
            </motion.article>

          </div>
        </div>
      </section>

      <section className="px-5 md:px-10 lg:px-16 py-8 md:py-10">
        <div className="mb-4 md:mb-5">
          <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-3">◇ PLATFORM CAPABILITIES</div>
          <h2
            className="font-serif text-ink leading-[1.1] whitespace-normal sm:whitespace-nowrap"
            style={{ fontSize: "clamp(20px, 2.7vw, 51px)" }}
          >
            Participant access, scoring, and tracking controls.
          </h2>
          <p className="text-base md:text-base text-ink-2 max-w-xl leading-relaxed mt-3">
            These platform panels reflect the delivery requirements for authentication, scoring automation,
            timing visibility, and participant profile management.
          </p>
        </div>

        <div className="grid grid-cols-12 gap-3 md:gap-4">
          {journeyCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <motion.article
                key={card.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.65, delay: index * 0.06 }}
                className="col-span-12 md:col-span-6 xl:col-span-4 glass rounded-sm overflow-hidden"
              >
                <div
                  className={`assessment-visual-panel assessment-visual-panel-${card.variant} min-h-[230px]`}
                  style={panelPhotoStyle(card.image)}
                />
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-9 w-9 rounded-full border border-gold/20 flex items-center justify-center text-gold">
                      <Icon className="w-4 h-4" />
                    </div>
                    <h3 className="font-serif text-[1.55rem] md:text-[1.75rem] leading-tight text-ink">{card.title}</h3>
                  </div>
                  <p className="text-base text-ink-2 leading-relaxed">{card.text}</p>
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>

      <section className="px-5 md:px-10 lg:px-16 py-8 md:py-10">
        <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-3">◇ PROFILES TO BE DIGITIZED</div>
        <h2
          className="font-serif text-ink leading-[1.1] mb-3 whitespace-normal sm:whitespace-nowrap"
          style={{ fontSize: "clamp(20px, 2.75vw, 51px)" }}
        >
          Digitization across six structured profile tools.
        </h2>
        <p className="text-ink-2 text-base md:text-base max-w-3xl leading-relaxed mb-4 md:mb-5">
          The platform is designed to support multiple spiritual and life-stage profiles through one consistent participant experience.
        </p>
        <div className="grid grid-cols-12 gap-3 md:gap-4">
          {featuredProfile ? (
            <motion.article
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="col-span-12 md:col-span-6 glass rounded-sm p-5"
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="font-serif text-[1.7rem] text-ink leading-tight">{featuredProfile.name}</div>
                  <div className="text-base text-ink-2 mt-1">
                    {featuredProfile.questionCount} items · {featuredProfile.factorCount} scored factors
                  </div>
                </div>
                <CheckCircle2 className="w-4 h-4 text-gold" />
              </div>
              <div className="font-mono text-[16px] tracking-widest-2 text-gold mt-4">CURRENTLY AVAILABLE</div>
            </motion.article>
          ) : null}

          {inviteOnlyCount > 0 ? (
            <motion.article
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="col-span-12 md:col-span-6 glass rounded-sm p-5"
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="font-serif text-[1.7rem] text-ink leading-tight">Profiles through your Redacamy course</div>
                  <div className="text-base text-ink-2 mt-1">
                    Each profile opens from the Redacamy course that includes it, once you have enrolled and paid.
                  </div>
                </div>
                <Lock className="w-4 h-4 text-ink-3" />
              </div>
              <div className="font-mono text-[16px] tracking-widest-2 text-gold mt-4">OPENS FROM YOUR REDACAMY COURSE</div>
            </motion.article>
          ) : null}
        </div>
      </section>
    </main>
  );
};

export default Landing;
